<template>
  <div v-if="data">
    <div class="container report-ready-page">
      <div>
        <img class="gyranian-image" src="../assets/gyranians-box.png" alt="Gyranians" />
      </div>
      <div>
        <h2>{{ headerText }}</h2>

        <h3 v-if="data.isSelfReview">
          You can access your Solo report by clicking the button below:
        </h3>

        <h3 v-else-if="data.subjectIsDummy || data.visibleOnlyToReviewer">
          The results of you work are ready for you to view. {{ data.subjectFirstName }} won't see
          those results unless you share them.
        </h3>

        <div v-else>
          <p style="margin-bottom: 25px" data-test="report-ready-text">
            Thank you so much for reviewing {{ data.subjectFirstName }}. We'll use the data you've
            given us to create a Viewpoints Report for {{ data.subjectFirstName }}. The report will
            pull together your view with others who have also given
            {{ data.subjectFirstName }} feedback. This will help bring focus to
            {{ data.subjectFirstName }}'s leadership development.
          </p>
          <p>You can see a full report on your view of {{ data.subjectName }} below.</p>
        </div>

        <a
          :href="`/soloreport/view/${props.id}`"
          data-test="report-ready-perspective-link"
          role="button"
          class="btn btn-default btn-lg center-flex"
          style="margin-bottom: 25px"
          >{{ reportButtonText }}</a
        >
        <div>
          <p>{{ continueYourJourneyText }}</p>
          <a
            :href="continueButtonLink"
            role="button"
            class="btn btn-primary btn-lg center-flex"
            data-test="report-ready-foundations-link"
            >{{ continueButtonText }}</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import useAxios from "../composables/api";
import { QuestionnaireApiModel } from "../api-models";

const props = defineProps<{ id: string }>();

const { data } = useAxios<QuestionnaireApiModel>(`/api/review/${props.id}`);

const reportButtonText = computed(() => {
  if (data.value?.isSelfReview) {
    return " Solo Report ";
  }
  if (data.value?.subjectIsDummy || data.value?.visibleOnlyToReviewer) {
    return " See results ";
  }
  return `Your View on ${data.value?.subjectName}`;
});

const headerText = computed(() => {
  if (data.value?.isSelfReview) return "Well done on completing a new Gyre self-review";
  return "Thank you!";
});

const continueButtonText = computed(() => {
  if (data.value?.reviewerHasCompletedFoundationsPathway) return "Continue your journey";
  return "Start Foundations Pathway";
});

const continueYourJourneyText = computed(() => {
  if (!data.value?.reviewerHasCompletedFoundationsPathway) {
    return "Continue your journey with Gyre. Click the button below and complete the Foundations pathway where you can map your own strengths and start your own development journey.";
  }

  return "Continue your journey with Gyre. Click the button below to go back to the Home page.";
});

const continueButtonLink = computed(() => {
  if (data.value?.reviewerHasCompletedFoundationsPathway) return "/home";
  return "/foundations/default";
});
</script>

<style scoped lang="less">
@import (reference) "../styles/bootstrap/variables.less";

.report-ready-page {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;

  & > div {
    flex: 1;
    padding: 1rem;
    width: 50%;
  }
}

.gyranian-image {
  width: 100%;
}

.open-access {
  margin: 10px 0;
}

@media (max-width: @screen-xs-max) {
  .report-ready-page {
    flex-direction: column-reverse;

    & > div {
      width: 100%;
    }
  }

  .gyranian-image {
    display: none;
  }
}
</style>
